import { useEffect, useState } from "react";
import { RootState } from "../../store/combineReducer";
import { useSelector } from "react-redux";
import LavorisService from "../../services/LavorisService";
import NotificationCard from "../../components/others/NotificationCard/NotificationCard";
import LatestAppointmentResultCard from "../../components/others/LatestAppointmentResultCard/LatestAppointmentResultCard";
import moment from "moment";
import StatisticsDashboard from "./stats";
import { useModal } from '../../hooks';
import RequestTurn from '../others/requestTurn';
import { useAlert } from '../../hooks/useAlert';
import CompanySelectorAsesor from "../../components/others/CompanySelectorAsesor";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "../../utils";
import Spinner from "../../components/others/Spinner/Spinner";

export default function Dashboard() {
  const {
    id,
    name,
    email,
    currentCompany,
    companies,
    currentRole,
    passwordExpiresAt
  } = useSelector((state: RootState) => state.user);

  const [notifications, setNotifications] = useState([]);
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [passExpirationDate, setPassExpirationDate] = useState<any>(null);
  const [statsData, setStatsData] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(
    currentCompany?.id || (companies && companies[0]?.id) || ""
  );
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const { closeModal, openModal, isActive } = useModal();

  const LavorisInstance = LavorisService.getInstance();

  const getName = () => {
    if (name) return name;
    if (currentCompany) return currentCompany.socialReason;
    if (companies) return companies[0].branchOfficeName || companies[0].socialReason;
    return email;
  };

  const isPassExpirationDateClose = () => {
    if (passwordExpiresAt) {
      const todayDate = new Date();
      const expiresAt = new Date(passwordExpiresAt);

      const differenceInTime = expiresAt.getTime() - todayDate.getTime();
      const differenceInDays = differenceInTime / (1000 * 3600 * 24);

      if (differenceInDays <= 15) {
        const formattedDate = `${expiresAt.getDate().toString().padStart(2, '0')}/${(expiresAt.getMonth() + 1).toString().padStart(2, '0')}/${expiresAt.getFullYear()}`;
        setPassExpirationDate(formattedDate);
      }
    }
  };

  const controller = new AbortController();
  const signal = controller.signal

  const getNotifications = async (signal: AbortSignal) => {
    try {
      const notifications: any =
        await LavorisInstance.NotificationsService.getPublishedNotifications(signal);
      // Ordenar las notificaciones por fecha más reciente
      const sortedNotifications = notifications.sort((a: any, b: any) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB.getTime() - dateA.getTime();
      });

      const latestNotifications = sortedNotifications.slice(0, 4); // Obtener las primeras 4 notificaciones ordenadas
      setNotifications(latestNotifications);
    } catch (e) {
      console.log(e);
    }
  };

  const getCompanyStats = async (companyId: string) => {
    setLoadingStats(true);
    try {
      const stats = await LavorisInstance.CompanyServices.getStats(companyId, signal);
      // 👇 Setear los datos de estadísticas
      setStatsData(stats);
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingStats(false);
    }
  };

  const fiveDaysAgo = new Date();
  fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 60);

  const days = ('0' + fiveDaysAgo.getDate()).slice(-2);
  const months = ('0' + (fiveDaysAgo.getMonth() + 1)).slice(-2);
  const years = fiveDaysAgo.getFullYear();

  const formattedDates = `${days}/${months}/${years}`;

  const currentDate = new Date();

  const day = ('0' + currentDate.getDate()).slice(-2);
  const month = ('0' + (currentDate.getMonth() + 1)).slice(-2);
  const year = currentDate.getFullYear();

  const formattedCurrentDate = `${day}/${month}/${year}`;

  function formatoFecha(fecha: any) {

    const partes = fecha?.split('/');
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  }

  const updateUserLoggedInPassword = async () => {
    try {
      setLoadingButton(true);
      const res = await LavorisInstance.User.updateUserLoggedInPassword();
      if (res) {
        navigate(`/Alta/Agregar-password/${res}`);
      }
    } catch (err) {
      showAlert(getErrorMessage({ title: 'Error al redirigir', err }));
    } finally{
      setLoadingButton(false);
    }
  }

  const getTurns = async () => {
    try {
      const fromParam = formattedDates;
      const toParam = formattedCurrentDate;
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      const turns: any = await LavorisInstance.TurnServices.getDashboardAppointmens(signal);
      if (turns) {
        const filteredData = turns?.filter((row: any) => {
          const dates = row.date;
          if (!dates && row.status === "Solicitado") return row.status
          if (!dates && row.status === "Eliminado") return false


          if (fromParam && toParam) {
            const date = new Date(formatoFecha(dates));
            const fromDate = new Date(formatoFecha(fromParam));
            const toDate = new Date(formatoFecha(toParam));


            const formattedDate = date.toISOString().slice(0, 19).replace("T", " ");
            const formattedFromDate = fromDate.toISOString().slice(0, 19).replace("T", " ");
            const formattedToDate = toDate.toISOString().slice(0, 19).replace("T", " ");

            return formattedDate >= formattedFromDate && formattedDate <= formattedToDate

          }
          return dates;

        });
        const sortedAppointments = filteredData?.sort((a: { date: moment.MomentInput; }, b: { date: moment.MomentInput; }) => {
          if (!a.date) return 1;
          if (!b.date) return -1;
          const dateA = moment(a.date, 'DD/MM/YYYY').toDate();
          const dateB = moment(b.date, 'DD/MM/YYYY').toDate();
          return dateB.getTime() - dateA.getTime();
        });


        setAppointmentsData(sortedAppointments);

      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    isPassExpirationDateClose();
  }, [])

  useEffect(() => {
    if (currentRole === 'ASESOR' && selectedCompanyId) {
      getCompanyStats(selectedCompanyId);
    } else if (companies.length && companies[0].id) {
      getCompanyStats(companies[0].id);
    }
  }, [selectedCompanyId, currentRole])

  // Actualizar selectedCompanyId cuando cambie currentCompany
  useEffect(() => {
    if (currentCompany?.id) {
      setSelectedCompanyId(currentCompany.id);
    }
  }, [currentCompany]);

  useEffect(() => {
    const getData = async () => {
      if (['RRHH', 'RRHH_APPOINTMENTS', 'COMPANY_ADMIN'].includes(currentRole)) {
        await getTurns();
        await getNotifications(signal);
      }
      if (['PARTICULAR_CLIENT',].includes(currentRole)) {
        await getNotifications(signal);
      }
      // ASESOR role doesn't need notifications or turns data
    }
    getData()
  }, [currentRole]);

  useEffect(() => {
    return () => {
      controller.abort()
    };
  }, [])

  const passRest = () => {
    return (
      <button
        type="button"
        onClick={() => updateUserLoggedInPassword()}
        className="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 transition-colors"
      >
        Actualizar contraseña AQUÍ
      </button>
    )
  }

  return (
    <>
      <div className="main-container">
        {passExpirationDate && !loadingButton && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-2">
            <h2 className="text-lg">
              Su contraseña caducará el: <strong>{passExpirationDate}</strong>{" "}
              {passRest()}
            </h2>
          </div>
        )}

        {passExpirationDate && loadingButton && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-2">
            <h2 className="text-lg">
              <Spinner/>
            </h2>
          </div>
        )}
        
        <h1 className="text-3xl mb-4">Hola {getName()}!</h1>

        {/* Selector de Compañía para ASESOR */}
        <CompanySelectorAsesor />

        {currentRole !== "PARTICULAR_CLIENT" && currentRole !== "ASESOR" ? (
          <div>
            <h6 className="subtitle">Estadísticas</h6>
            <StatisticsDashboard
              statsData={statsData}
              loading={loadingStats}
            />
          </div>
        ) : currentRole === "ASESOR" ? (
          <div>
            <h6 className="subtitle">Estadísticas</h6>
            <StatisticsDashboard
              statsData={statsData}
              loading={loadingStats}
            />
          </div>
        ) : (
          <div className="mb-3">
            <div className="flex gap-4">
              <button
                onClick={() => {
                  openModal("modalRequestTurn", id)
                }}
                className="flex-1 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-8 rounded-lg transition-colors duration-200"
                style={{ minHeight: 120 }}
              >
                <svg width={32} height={32} className="mr-3" stroke="currentColor" fill="none" strokeWidth={2} viewBox="0 0 24 24">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Pedir un nuevo turno
              </button>
              <button
                className="flex-1 flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-semibold py-8 rounded-lg transition-colors duration-200"
                style={{ minHeight: 120 }}
                onClick={() => window.location.href = "/Resultados"}
              >
                <svg width={32} height={32} className="mr-3" stroke="currentColor" fill="none" strokeWidth={2} viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                Ver mis resultados
              </button>
            </div>
          </div>
        )}

        {(currentRole === "PARTICULAR_CLIENT" && notifications.length > 0) && (
          <NotificationCard
            notifications={notifications}
          />
        )}

        {
          ['RRHH', 'RRHH_APPOINTMENTS', 'COMPANY_ADMIN'].includes(currentRole) && !['ASESOR'].includes(currentRole) && (notifications.length > 0 || appointmentsData.length > 0) &&
          <>
            <NotificationCard
              notifications={notifications}
            />

            <LatestAppointmentResultCard
              appointmentsData={appointmentsData}
            />
          </>
        }
        {
          isActive("modalRequestTurn") && (
            <RequestTurn
              toggleModal
              onClose={() => closeModal('modalRequestTurn')}
              urlTittle={"modalRequestTurn"}
              refreshFunction={() => {
                closeModal('modalRequestTurn');
                showAlert('El turno se solicitó con exito. Lavoris se contactará para comunicarle el día y el horario del turno')

              }}
              patientType="PARTICULAR_CLIENT"
            />
          )}
      </div>
    </>
  );
}
